/**
 * Auth Context - Session-based authentication with WebAuthn
 *
 * Provides auth state and actions (register, login, logout) to the app.
 * Uses TanStack Query to manage session state.
 */
import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { ApiClientError, type AuthUser } from '@wovendays/api-client';
import { apiClient } from '@/lib/api-client';
import { posthog } from '@/lib/posthog';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  register: (email: string, name: string) => Promise<void>;
  login: (email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Check session on mount
  const { data: sessionData, isLoading } = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: async () => {
      try {
        const data = await apiClient.get<{ user: AuthUser }>('/api/auth/session');
        posthog.identify(data.user.id, { email: data.user.email, name: data.user.name });
        return data.user;
      } catch (e) {
        if (e instanceof ApiClientError && e.status === 401) {
          return null;
        }
        throw e;
      }
    },
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const user = sessionData ?? null;
  const isAuthenticated = user !== null;

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async ({ email, name }: { email: string; name: string }) => {
      // Step 1: Get registration options
      const { options } = await apiClient.post<{ options: any }>(
        '/api/auth/passkey/register/options',
        { email, name }
      );

      // Step 2: Create credential with browser WebAuthn API
      const credential = await startRegistration({ optionsJSON: options });

      // Step 3: Verify with server
      const result = await apiClient.post<{ verified: boolean; user: AuthUser }>(
        '/api/auth/passkey/register/verify',
        { userId: options.user.id, email, name, credential }
      );

      return result.user;
    },
    onSuccess: (user) => {
      queryClient.setQueryData(['auth', 'session'], user);
    },
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      // Step 1: Get authentication options
      const { options } = await apiClient.post<{ options: any }>(
        '/api/auth/passkey/login/options',
        { email }
      );

      // Step 2: Authenticate with browser WebAuthn API
      const credential = await startAuthentication({ optionsJSON: options });

      // Step 3: Verify with server
      const result = await apiClient.post<{ verified: boolean; user: AuthUser }>(
        '/api/auth/passkey/login/verify',
        { email, credential }
      );

      return result.user;
    },
    onSuccess: (user) => {
      queryClient.setQueryData(['auth', 'session'], user);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/api/auth/logout');
    },
    onSuccess: () => {
      posthog.reset();
      queryClient.setQueryData(['auth', 'session'], null);
      queryClient.clear();
    },
  });

  const register = useCallback(
    async (email: string, name: string) => {
      await registerMutation.mutateAsync({ email, name });
    },
    [registerMutation]
  );

  const login = useCallback(
    async (email: string) => {
      await loginMutation.mutateAsync({ email });
    },
    [loginMutation]
  );

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        register,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
